import { cloneDeep, difference, find, isEmpty, uniq } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { SlashIcon } from 'tdesign-icons-react';
import { Breadcrumb, Menu } from 'tdesign-react';
import qs from 'qs';
import { useMemoizedFn } from 'ahooks';
import { getRootPath, isVoid } from '@/utils/common';
import { getMenuIcon } from '@/slices/layoutSlice';
import { doBuried } from '@/services/buriedService';
import PlusarCulsterAddon from './PlusarClusterAddon';
import KOPClusterAddon from './KOPClusterAddon';
import { isKafkaCluster } from './CulsterAddon';
import BackupClusterTag from './BackupClusterTag';

const { MenuItem, SubMenu } = Menu;
const { BreadcrumbItem } = Breadcrumb;

const findFirstClickableMenu = (menuData) => {
  let found;

  const foundMenu = menuData[0];
  if (isEmpty(foundMenu?.children)) {
    found = foundMenu;
  } else {
    found = findFirstClickableMenu(foundMenu.children);
  }

  return found;
};

// const findMenuByPath = (menuData, path) => {
//   let found;
//   menuData.forEach((menu) => {
//     if (menu.path === path) {
//       found = menu;
//     }
//     if (!isEmpty(menu.children)) {
//       const foundInchildren = findMenuByPath(menu.children, path);
//       if (foundInchildren) {
//         found = foundInchildren;
//       }
//     }
//   });

//   return found;
// };

const findMenuById = (menuData, id) => {
  let found;
  menuData.forEach((menu) => {
    if (menu.id === id) {
      found = menu;
    }
    if (!isEmpty(menu.children)) {
      const foundInchildren = findMenuById(menu.children, id);
      if (foundInchildren) {
        found = foundInchildren;
      }
    }
  });

  return found;
};

// 子页面菜单布局
function MenuLayout({ menus, isKafka }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(window.innerWidth < 1400);
  const [expanded, setExpanded] = useState([]);
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const { pathname } = useLocation();

  // 根路径
  const rootPath = useMemo(
    () => find(menus, { path: getRootPath(pathname) })?.path,
    [menus, pathname],
  );

  // 需要集群选择插件的根菜单
  const showCluster = useMemo(() => {
    return ['/cluster', '/kop-cluster', '/dev', '/pro', '/kop-cluster'].includes(rootPath);
  }, [rootPath]);

  // 根菜单
  const rootMenu = useMemo(() => {
    return find(menus, { path: getRootPath(pathname) });
  }, [menus, pathname]);

  // 当前菜单
  const currentMenuData = useMemo(() => {
    return rootMenu?.children || [];
  }, [rootMenu]);

  // 当前菜单值value, 面包屑breads
  const selected =
    useMemo(() => {
      const cloneMenus = cloneDeep(currentMenuData);
      const findMenu = (menuArray, breads) => {
        let _menu;
        for (const menu of menuArray) {
          const _breads = breads.concat(menu); // 保留父节点+自身作为面包屑备用
          if (pathname.indexOf(menu.path) === 0) {
            menu.breads = _breads;
            _menu = menu;
          }
          if (menu.children && menu.children.length) {
            const found = findMenu(menu.children, _breads);
            if (found) {
              _menu = found;
            }
          }
        }
        return _menu;
      };

      return findMenu(cloneMenus, [rootMenu]);
    }, [pathname, currentMenuData, rootMenu]) || {};

  const { id: value, breads = [] } = selected;

  const goPage = useMemoizedFn((path) => {
    if (!path) return;
    if (showCluster && clusterName) {
      // 集群页面如有name query，则带入name
      const query = qs.stringify({ name: clusterName }, { addQueryPrefix: true });
      navigate(path + query);
      return;
    }
    navigate(path);
  });

  const onExpand = useMemoizedFn((array, isAdd) => {
    let expanded = array;
    setExpanded((origin) => {
      // 如果是添加到已经打开的菜单中
      if (isAdd) {
        expanded = uniq([...origin, ...array]);
      }
      difference(expanded, origin).forEach((id) => {
        // 新打开的menu, 埋点
        const toBuriedMenu = findMenuById(currentMenuData, id);
        if (toBuriedMenu && toBuriedMenu.eventId) {
          doBuried({
            eventId: toBuriedMenu.eventId,
          });
        }
      });
      return expanded;
    });
  });

  // 空页面尝试定位第一个子路由
  useEffect(() => {
    if (!value) {
      const found = findFirstClickableMenu(currentMenuData);
      if (!found) {
        return;
      }
      const { parent, path, eventId } = found;
      // 数据埋点
      if (eventId) {
        doBuried({
          eventId,
        });
      }
      goPage(path);
      if (parent) {
        onExpand([parent.id]);
      }
    } else {
      // 查找没有展开的菜单并展开
      const { parent } = findMenuById(currentMenuData, value);
      onExpand([parent.id], true);
    }
  }, [value, navigate, currentMenuData, goPage, onExpand]);

  // 菜单节点
  const menuNodes = useMemo(() => {
    const getMenu = (menuArray) => {
      const _menus = [];
      for (const menu of menuArray) {
        const icon = getMenuIcon(menu.icon);
        if (menu.children && menu.children.length) {
          // kafka集群下，不展示bookkeeper 、 zookeeper 、 tenant 、 namespace
          if (
            [
              'kop-cluster/bookkeeper',
              'kop-cluster/zookeeper',
              'kop-cluster/tenant',
              'kop-cluster/namespace',
            ].includes(menu.id) &&
            isKafka
          ) {
            continue;
          }
          _menus.push(
            <SubMenu key={menu.label} value={menu.id} title={menu.label} icon={icon}>
              {getMenu(menu.children)}
            </SubMenu>,
          );
          continue;
        }

        _menus.push(
          <MenuItem
            value={menu.id}
            key={menu.path}
            onClick={({ e }) => {
              e.preventDefault();
              // 数据埋点
              if (menu.eventId) {
                doBuried({
                  eventId: menu.eventId,
                });
              }
              goPage(menu.path);
            }}
            href={
              showCluster
                ? menu.path + qs.stringify({ name: clusterName }, { addQueryPrefix: true })
                : menu.path
            }
            icon={icon}
          >
            {menu.label}
          </MenuItem>,
        );
      }
      return _menus;
    };

    return getMenu(currentMenuData);
  }, [currentMenuData, goPage, clusterName, showCluster, isKafka]);

  const addon = useMemo(() => {
    if (!showCluster) return null;

    if (rootPath === '/kop-cluster') {
      return <KOPClusterAddon collapsed={collapsed} clusterType='kop' />;
    }

    return <PlusarCulsterAddon collapsed={collapsed} clusterType='plusar' />;
  }, [collapsed, showCluster, rootPath]);

  return (
    <Root className='fadeIn'>
      <Left>
        {addon}
        <Menu
          style={{ flex: 1, height: 0 }}
          width={['200px']}
          value={value}
          collapsed={collapsed}
          expanded={expanded}
          onExpand={onExpand}
        >
          {menuNodes}
        </Menu>
        <ToggleButton
          icon={collapsed ? require('./images/isClose.png') : require('./images/isOpen.png')}
          onClick={() => setCollapsed(!collapsed)}
        />
      </Left>
      <Vr />
      <Body>
        <BackupClusterTag rootPath={rootPath} />
        <Breadcrumb maxItemWidth='200px' separator={<SlashIcon />}>
          {breads
            .filter((bread) => !isVoid(bread.path))
            .map((bread, index) => (
              // 根节点不触发事件
              <BreadcrumbItem onClick={() => index !== 0 && goPage(bread.path)} key={index}>
                {bread.label}
              </BreadcrumbItem>
            ))}
        </Breadcrumb>
        <Container className='narrow-scrollbar body-container'>
          <Outlet />
        </Container>
      </Body>
    </Root>
  );
}

export default connect((state) => ({
  menus: state.layout.menus,
  isKafka: isKafkaCluster(state.kopCluster.allList), // 判断是不是kafka节点集群
}))(MenuLayout);

const Root = styled.div`
  display: flex;
  height: 100%;
  .t-default-menu {
    background-color: transparent;
    .t-icon + .t-menu__item-link {
      margin-left: 0;
    }
    svg {
      color: var(--td-font-gray-1);
    }
    .t-is-active:not(.t-is-opened) {
      svg {
        color: var(--td-brand-color-7);
      }
    }
    .t-menu__item.t-is-active:not(.t-is-opened) {
      background: rgba(25, 77, 255, 0.08);
      .t-menu__content {
        color: #0039ff;
      }
    }
  }
  .t-menu__content {
    margin-left: 16px;
    color: var(--td-font-gray-1);
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Vr = styled.div`
  width: 2px;
  background-color: #edeff2;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  width: 0;
  flex-direction: column;
  padding: 20px 0 0 20px;
  box-sizing: border-box;
  position: relative;
`;

const Container = styled.div`
  margin-top: 8px;
  padding-bottom: 16px;
  flex: 1;
  overflow-y: auto;
  padding-top: 6px;
`;

const ToggleButton = styled.div`
  cursor: pointer;
  background-image: url(${(props) => props.icon});
  background-size: 12px 36px;
  background-position-y: 10px;
  background-repeat: no-repeat;
  width: 12px;
  height: 36px;
  padding: 10px; // 扩大点击范围
  box-sizing: content-box;
  position: absolute;
  right: -32px;
  top: 0;
  bottom: 0;
  z-index: 1;
  margin: auto;
`;
